/**
 * @fileoverview "Gerenciar Triagens" (/fluxo, admin): lista das triagens com o
 * editor e o acompanhamento das triagens em andamento. Fechar ou voltar com
 * mudanças não salvas pede confirmação. Com a regra nova ligada, cada aba pela
 * permissão dela: a lista e o editor, triage.configure; o acompanhamento,
 * triage.monitor.
 */

import React, { useCallback, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { ListChecks, X } from "lucide-react";

import { i18n } from "../../../../translate/i18n";
import usePermission from "../../../../hooks/usePermission";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import useStyles from "./styles";
import DefinitionList from "./DefinitionList";
import DefinitionEditor from "./DefinitionEditor";
import SessionsMonitor from "./SessionsMonitor";

const TABS = { definitions: "triage.configure", sessions: "triage.monitor" };

const TriageManager = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const { can } = usePermission();
  const tabs = Object.keys(TABS).filter((key) => can(TABS[key], true));
  const [tab, setTab] = useState("definitions");
  const current = tabs.includes(tab) ? tab : tabs[0];
  // undefined: lista; null: nova; número: editando essa.
  const [editing, setEditing] = useState(undefined);
  const [confirm, setConfirm] = useState(null);
  const dirtyRef = useRef(false);

  const setDirty = useCallback((dirty) => {
    dirtyRef.current = dirty;
  }, []);

  const sairDoEditor = (depois) => {
    if (dirtyRef.current) {
      setConfirm(() => depois);
      return;
    }
    depois();
  };

  const voltarParaLista = () => {
    dirtyRef.current = false;
    setEditing(undefined);
  };

  const fechar = () => {
    dirtyRef.current = false;
    setEditing(undefined);
    setTab("definitions");
    onClose();
  };

  const handleClose = () => (editing !== undefined ? sairDoEditor(fechar) : fechar());

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" fullScreen={fullScreen} classes={{ paper: classes.paper }}>
      {editing !== undefined ? (
        <DefinitionEditor
          key={editing ?? "new"}
          definitionId={editing}
          onBack={() => sairDoEditor(voltarParaLista)}
          onClose={handleClose}
          onDirtyChange={setDirty}
        />
      ) : (
        <>
          <div className={classes.header}>
            <div className={classes.title}>
              <ListChecks size={20} strokeWidth={1.75} />
              {i18n.t("triage.manager.title")}
            </div>
            <button type="button" className={classes.iconBtn} onClick={handleClose} aria-label={i18n.t("triage.dialogs.back")}>
              <X />
            </button>
          </div>
          <div className={classes.tabs}>
            {tabs.map((key) => (
              <button
                key={key}
                type="button"
                className={`${classes.tab} ${current === key ? classes.tabActive : ""}`}
                onClick={() => setTab(key)}
              >
                {i18n.t(`triage.manager.tabs.${key}`)}
              </button>
            ))}
          </div>
          <div className={classes.body}>
            {current === "definitions" && <DefinitionList onEdit={(id) => setEditing(id)} onNew={() => setEditing(null)} />}
            {current === "sessions" && <SessionsMonitor onNavigate={fechar} />}
          </div>
        </>
      )}

      <ConfirmationModal
        title={i18n.t("triage.manager.editor.unsaved")}
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const depois = confirm;
          setConfirm(null);
          if (depois) depois();
        }}
      >
        {i18n.t("triage.manager.editor.discardConfirm")}
      </ConfirmationModal>
    </Dialog>
  );
};

export default TriageManager;
